import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProductTitleWithLink.module.scss';

interface ProductTitleWithLinkProps {
  productName: string;
  readonly?: boolean;
}

/**
 * Parse productName to extract parent moment link
 * Format: "Firefly.com Web (FF)-ARM-817"
 * Returns: { productPart: "Firefly.com Web (FF)", issueId: "ARM-817" }
 */
const parseProductName = (
  productName: string
): { productPart: string; issueId: string } | null => {
  if (!productName) return null;

  // Find the first hyphen that separates product name from issue ID
  const firstHyphenIndex = productName.indexOf('-');
  if (firstHyphenIndex === -1) return null;

  const productPart = productName.substring(0, firstHyphenIndex);
  const issueId = productName.substring(firstHyphenIndex + 1);

  // Validate issueId format (should be like ARM-817, not just any text)
  if (!issueId || issueId.length < 3) return null;

  return { productPart, issueId };
};

/**
 * ProductTitleWithLink Component
 * Displays product name with clickable parent moment link in readonly mode
 * Falls back to plain product name in editable mode
 */
export const ProductTitleWithLink: React.FC<ProductTitleWithLinkProps> = ({
  productName,
  readonly = false
}) => {
  const navigate = useNavigate();

  // Only parse and create link in readonly mode
  if (!readonly) {
    return <>{productName || 'Product'}</>;
  }

  const parsed = parseProductName(productName);

  // If parsing failed, show plain product name
  if (!parsed) {
    return <>{productName || 'Product'}</>;
  }
  const navigateToParent = (issueId: string): void => {
    navigate(`/moments/${issueId}`, { replace: false });
    scrollToFeatures();
  };

  const scrollToFeatures = () => {
    setTimeout(() => {
      const featuresSection = document.querySelector(
        '[data-testid="features-section-toggle-header"]'
      );

      if (featuresSection) {
        featuresSection.scrollIntoView({
          block: 'center'
        });
      }
    }, 1600);
  };

  // Handle navigation to parent moment
  const handleNavigateToParent = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateToParent(parsed.issueId);
  };

  // Handle keyboard navigation (Enter or Space key)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      navigateToParent(parsed.issueId);
    }
  };

  // Return product name with clickable link (space-separated)
  return (
    <span>
      {parsed.productPart}{' '}
      <span
        className={styles.parentLink}
        onClick={handleNavigateToParent}
        role="link"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {parsed.issueId}
      </span>
    </span>
  );
};

export default ProductTitleWithLink;
